declare global {
    interface Window {
        __ENV__?: Record<string, string>;
    }
}

// Populated at container startup from real runtime env vars (see
// generate-env-config.sh + public/env-config.js) -- returns undefined in
// local dev, where the existing import.meta.env.VITE_*/.env fallbacks below
// still apply unchanged.
export const runtimeEnv = (key: string): string | undefined => window.__ENV__?.[key];

// For the common "same key, both sources" case (runtime var falls back to
// the same-named import.meta.env var in local dev). Fields with bespoke
// fallback logic (see API_URL, ANALYTICS_URL below) stay hand-written.
export const runtimeEnvOr = <K extends keyof ImportMetaEnv>(runtimeKey: string, importMetaKey: K): string | undefined =>
    runtimeEnv(runtimeKey) || (import.meta.env[importMetaKey] as string | undefined);

const PUBLIC_URL = runtimeEnv("PUBLIC_URL") != null ? runtimeEnv("PUBLIC_URL") : "";

const ORGA_URL = runtimeEnv("REACT_APP_ORGA_URL") != null ? runtimeEnv("REACT_APP_ORGA_URL") : "";

const RESET_PASSWORD_URL =
    runtimeEnv("REACT_APP_RESET_PASSWORD_URL") != null
        ? runtimeEnv("REACT_APP_RESET_PASSWORD_URL")
        : "";

const DOMAIN = runtimeEnv("REACT_APP_DOMAIN") != null ? runtimeEnv("REACT_APP_DOMAIN") : "";

const API_URL =
    runtimeEnv("REACT_APP_TEAM_API") || (import.meta.env.DEV && import.meta.env.VITE_API_URL);

const FLAGS_API_URL =
    runtimeEnv("REACT_APP_FLAGS_API_URL") != null ? runtimeEnv("REACT_APP_FLAGS_API_URL") : "";

const FLAGS_API_KEY =
    runtimeEnv("REACT_APP_FLAGS_API_KEY") != null ? runtimeEnv("REACT_APP_FLAGS_API_KEY") : "";

const ANALYTICS_URL =
    runtimeEnv("REACT_APP_ANALYTICS_URL") ||
    (typeof import.meta.env.VITE_API_URL === "string"
        ? import.meta.env.VITE_API_URL.replace("team-api", "analytics")
        : "") ||
    "";

const JOB_POSTING_URL =
    runtimeEnv("REACT_APP_JOB_POSTING_URL") != null ? runtimeEnv("REACT_APP_JOB_POSTING_URL") : "";

const stripePublicKey = runtimeEnv("REACT_APP_STRIPE_PUBLIC_KEY");
const STRIPE_PUBLIC_KEY = stripePublicKey != null ? stripePublicKey : "pk_test_5GdRDKdNtzOgNgskESc6Y9mM";

export const CUSTOMERIO_WRITE_KEY =
    runtimeEnv("REACT_APP_CUSTOMERIO_WRITE_KEY") ?? import.meta.env.VITE_CUSTOMERIO_WRITE_KEY ?? "";
export const CUSTOMERIO_SITE_ID =
    runtimeEnv("REACT_APP_CUSTOMERIO_SITE_ID") ?? import.meta.env.VITE_CUSTOMERIO_SITE_ID ?? "";

export default {
    PUBLIC_URL,
    ORGA_URL,
    RESET_PASSWORD_URL,
    API_URL,
    FLAGS_API_URL,
    FLAGS_API_KEY,
    DOMAIN,
    ANALYTICS_URL,
    JOB_POSTING_URL,
    STRIPE_PUBLIC_KEY,
    CUSTOMERIO_WRITE_KEY,
    CUSTOMERIO_SITE_ID,
};

export const AlgoliaConfig = {
    appId: runtimeEnv("REACT_APP_ALGOLIA_APP_ID") != null ? runtimeEnv("REACT_APP_ALGOLIA_APP_ID") : "B1ARJ7GA5A",
    apiKey:
        runtimeEnv("REACT_APP_ALGOLIA_APP_KEY") != null
            ? runtimeEnv("REACT_APP_ALGOLIA_APP_KEY")
            : "9124287257a8d22cfe9169bf0fc139a6",
    organisationsIndexName:
        runtimeEnv("REACT_APP_ALGOLIA_ORGANISATION_INDEX") != null
            ? runtimeEnv("REACT_APP_ALGOLIA_ORGANISATION_INDEX")
            : "dev_organisation",
};

if (!window.location.origin) {
    // Typescript is complaining about origin being a readonly property, which
    // is not true, just the recommended usage. We could rewrite this at some point.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.location.origin = `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ""
    }`;
}

const location = window.location.origin;
export const isDev = location.indexOf(`dev`) !== -1;
export const isStaging = location.indexOf(`staging`) !== -1;
export const isLocal = location.indexOf(`jumper`) !== -1 || location.indexOf(`localhost`) !== -1;
export const isProd = !isLocal && !isDev && !isStaging;
export const ENVIRONMENT =
    (isDev && `development`) || (isStaging && `staging`) || (isLocal && `local`) || `production`;
